import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';

import {
  Input,
  BaseButton,
  CloseButton,
  LocationAutoCompleteList,
  useLocationSelection,
  CustomDatePicker,
  VisaTypeList,
  GenderList,
  useAuth,
  VisibilityRadioGroup,
  KoreanSkillsList,
  TopikSpeakingList,
  TopikList,
} from '@repo/common';
import NationalityInputBlock from '../../../Career/Steps/ProfileStep/PersonalInfo/DynamicFields/NationalityInputBlock';
import RoleList from '../../../JobBoard/Post/JobInfo/RoleList';
import CareerList from '../../../Career/Steps/ProfileStep/PersonalInfo/CareerList';

import { ReactComponent as LocationIcon } from '@repo/common/assets/icons/16px/icon_16px_location.svg';
import { ReactComponent as CalendarIcon } from '@repo/common/assets/icons/16px/icon_16px_calendar_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import { ReactComponent as EditIcon } from '@repo/common/assets/icons/20px/icon_20px_edit_filled.svg';
import { ReactComponent as TrashIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';

const getInputState = (focused, clicked) => {
  if (clicked) return 'active';
  if (focused) return 'focused';
  return 'default';
};

const getIconState = (value, fieldState) => {
  if (value instanceof Date || value) return 'active';
  if (fieldState === 'active' || fieldState === 'focused') return 'focused';
  return 'default';
};

const StyledLocationIcon = ({ $state }) => {
  const cls =
    $state === 'active'
      ? 'text-icon-primary'
      : $state === 'focused'
        ? 'text-icon-placeholder'
        : 'text-icon-secondary';
  return <LocationIcon className={cls} />;
};

const StyledCalendarIcon = ({ $state }) => {
  const cls =
    $state === 'active'
      ? 'text-icon-primary'
      : $state === 'focused'
        ? 'text-icon-placeholder'
        : 'text-icon-secondary';
  return <CalendarIcon className={cls} />;
};

const mergeVisibility = (user, field, value) => ({
  ...(user?.visibility ?? {}),
  [field]: (value || 'PUBLIC').toUpperCase(),
});

const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return {
    open,
    ref,
    toggle: () => setOpen((o) => !o),
    close: () => setOpen(false),
  };
};

const EditorActions = ({ onSave, disabled }) => (
  <div className='flex justify-start'>
    <BaseButton onClick={onSave} isDisabled={disabled} hideIcon>
      Save changes
    </BaseButton>
  </div>
);

const ArrowToggleIcon = ({ open }) =>
  open ? (
    <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
  ) : (
    <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
  );

const TopikCertModal = ({ topikInfo, setTopikInfo, onClose }) => {
  const levelDd = useDropdown();
  const [dateFocused, setDateFocused] = useState(false);

  if (!topikInfo) return null;

  return (
    <>
      <div
        className='fixed inset-0 z-[999] bg-surface-black40'
        onClick={onClose}
      />

      <div className='fixed bottom-0 left-0 right-0 z-[1000] flex flex-col bg-grayscale-gray00 rounded-t-[30px] pt-4 px-6 pb-10 w-full'>
        <div className='pb-6'>
          <CloseButton onClick={onClose} size='w-8 h-8' type='light' />
        </div>

        <div className='flex flex-col gap-10 pb-[60px]'>
          <div className='flex flex-col items-start gap-2'>
            <div className='typo-title1 text-text-primary'>
              Add certification
            </div>
            <div className='typo-b14 text-grayscale-gray04'>
              This information will be used in your resume.
            </div>
          </div>

          <div className='flex flex-col gap-4 w-full'>
            {/* TOPIK Level */}
            <div className='relative w-full' ref={levelDd.ref}>
              <div className='flex flex-col gap-2'>
                <div className='typo-b12Strong text-text-placeholder'>
                  {topikInfo.type === 'topik'
                    ? 'TOPIK Level'
                    : 'TOPIK speaking Level'}
                </div>
                <Input
                  label=''
                  placeholder='Select level'
                  value={topikInfo.level}
                  readOnly
                  state={getInputState(false, levelDd.open)}
                  rightIcon={<ArrowToggleIcon open={levelDd.open} />}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={levelDd.toggle}
                  onRightIconClick={levelDd.toggle}
                />

                {levelDd.open && topikInfo.type === 'topik' && (
                  <div className='z-10 w-full'>
                    <TopikList
                      selectedOption={topikInfo.level}
                      onSelect={(opt) => {
                        setTopikInfo((prev) => ({ ...prev, level: opt }));
                        levelDd.close();
                      }}
                      isMyPage={true}
                    />
                  </div>
                )}

                {levelDd.open && topikInfo.type === 'topikSpeaking' && (
                  <div className='z-10 w-full'>
                    <TopikSpeakingList
                      selectedOption={topikInfo.level}
                      onSelect={(opt) => {
                        setTopikInfo((prev) => ({ ...prev, level: opt }));
                        levelDd.close();
                      }}
                      isMyPage={true}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Issue date */}
            <div className='flex flex-col gap-2 w-[180px]'>
              <div className='typo-b12Strong text-text-placeholder'>
                Issue date
              </div>
              <Input
                id='topik-date-input'
                label=''
                value={topikInfo.date}
                placeholder='YYYY / MM / DD'
                state={getInputState(false, dateFocused)}
                hideCount
                showStatusIcon={false}
                onChange={(e) =>
                  setTopikInfo((prev) => ({ ...prev, date: e.target.value }))
                }
                onFocus={() => setDateFocused(true)}
                onBlur={() => setDateFocused(false)}
              />
            </div>
          </div>
        </div>

        <BaseButton
          type='primary'
          size='l'
          hideIcon
          width='100%'
          onClick={onClose}
        >
          Save
        </BaseButton>
      </div>
    </>
  );
};

/* ================================================================== */
/* Name                                                               */
/* ================================================================== */

export const NameEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    onSave({ firstName: firstName.trim(), lastName: lastName.trim() });
  };

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <div className='flex flex-col gap-4 w-full'>
        <div className='flex-1'>
          <Input
            id='first-name-input'
            label='First name*'
            value={firstName}
            hideCount
            onChange={(e) => {
              setFirstName(e.target.value);
              if (error) setError('');
            }}
            placeholder='First name'
            className='placeholder:opacity-0'
            state={error ? 'failed' : 'default'}
            message={error || undefined}
          />
        </div>
        <div className='flex-1'>
          <Input
            id='last-name-input'
            label='Last name'
            value={lastName}
            hideCount
            showStatusIcon={false}
            onChange={(e) => setLastName(e.target.value)}
            placeholder='Last name'
            className='placeholder:opacity-0'
          />
        </div>
      </div>
      <EditorActions onSave={handleSave} />
    </div>
  );
};

/* ================================================================== */
/* Location                                                           */
/* ================================================================== */

export const LocationEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [location, setLocation] = useState(user?.location || '');
  const [visibility, setVisibility] = useState(
    user?.visibility?.location || 'PUBLIC',
  );
  const [focused, setFocused] = useState(false);
  const [results] = useState([]);
  const ref = useRef(null);

  const fieldState = getInputState(focused, false);
  const iconState = getIconState(location, fieldState);

  const selectItem = (item) => {
    setLocation(`${item.name}${item.country ? `, ${item.country}` : ''}`);
    setFocused(false);
  };

  const manualEnter = (q) => {
    setLocation(q);
    setFocused(false);
  };

  const field = useLocationSelection({
    value: location,
    onChange: (e) => setLocation(e.target.value),
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onSelect: selectItem,
    onManualEnter: manualEnter,
  });

  const handleSave = () =>
    onSave({
      location,
      visibility: mergeVisibility(user, 'location', visibility),
    });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <div className='w-full' ref={ref}>
        <Input
          id='location-input'
          type='text'
          label=''
          placeholder='Type your city or country'
          value={field.displayValue}
          onChange={field.handleChange}
          onFocus={field.handleFocus}
          onBlur={field.handleBlur}
          hideCount
          forceActiveBorder={fieldState !== 'default'}
          leftIcon={<StyledLocationIcon $state={iconState} />}
          rightIcon={
            focused && field.displayValue ? (
              <button
                onMouseDown={field.handleClear}
                type='button'
                className='cursor-pointer flex items-center justify-center w-5 h-5 bg-grayscale-gray01 border-none rounded-[30px] hover:opacity-80 transition-opacity'
              >
                <CancelIcon className='w-4 h-4' />
              </button>
            ) : null
          }
        />

        {focused && field.displayValue.length >= 2 && (
          <div className='mt-1 z-10 max-h-[calc(100vh-60px)] w-full'>
            <LocationAutoCompleteList
              searchText={field.displayValue}
              results={results}
              onSelect={field.handleSelect}
              onManualEnter={field.handleManualEnter}
              isMyPage={true}
              isMobile={true}
            />
          </div>
        )}
      </div>
      <VisibilityRadioGroup
        value={visibility}
        onChange={setVisibility}
        disabled={!location}
      />
      <EditorActions onSave={handleSave} disabled={!location.trim()} />
    </div>
  );
};

/* ================================================================== */
/* Birthday                                                           */
/* ================================================================== */

export const BirthdayEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [birthday, setBirthday] = useState(
    user?.birthday && !isNaN(new Date(user.birthday))
      ? new Date(user.birthday)
      : null,
  );
  const [visibility, setVisibility] = useState(
    user?.visibility?.birthday || 'PUBLIC',
  );
  const [pickerOpen, setPickerOpen] = useState(true);

  const buildPayload = (date) => ({
    birthday: date ? format(date, 'yyyy-MM-dd') : null,
    visibility: mergeVisibility(user, 'birthday', visibility),
  });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <Input
        id='birthday-input'
        label=''
        placeholder='Select your birthday'
        value={birthday ? format(birthday, 'dd MMM yyyy') : ''}
        readOnly
        hideCount
        showStatusIcon={false}
        visibility={user?.visibility?.birthday}
        onClick={() => setPickerOpen(true)}
        rightIcon={<StyledCalendarIcon $state={getIconState(birthday)} />}
        onRightIconClick={() => setPickerOpen(true)}
      />

      {pickerOpen && (
        <CustomDatePicker
          $isMobile
          isMyPagePersonalInfo
          selectedDate={birthday}
          onChange={(date) => setBirthday(date)}
          onClose={() => setPickerOpen(false)}
          isPrivate={visibility === 'PRIVATE'}
          onPrivateChange={(next) => setVisibility(next ? 'PRIVATE' : 'PUBLIC')}
          onSave={(date) => onSave(buildPayload(date))}
        />
      )}
    </div>
  );
};

/* ================================================================== */
/* Gender                                                             */
/* ================================================================== */

export const GenderEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [gender, setGender] = useState(
    user?.gender && user.gender !== 'null' ? user.gender : '',
  );
  const [visibility, setVisibility] = useState(
    user?.visibility?.gender || 'PUBLIC',
  );
  const [isCustom, setIsCustom] = useState(false);
  const dd = useDropdown();
  const inputRef = useRef(null);

  const handleSave = () =>
    onSave({ gender, visibility: mergeVisibility(user, 'gender', visibility) });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <div className='w-full' ref={dd.ref}>
        <Input
          id='gender-input'
          ref={inputRef}
          label=''
          placeholder={!isCustom ? 'Select your gender' : 'What’s your gender?'}
          value={gender}
          state={getInputState(false, dd.open)}
          readOnly={!isCustom}
          onClick={dd.toggle}
          onChange={(e) => setGender(e.target.value)}
          hideCount
          showStatusIcon={false}
          rightIcon={<ArrowToggleIcon open={dd.open} />}
          onRightIconClick={dd.toggle}
        />

        {dd.open && (
          <div className='mt-1 z-10 w-full'>
            <GenderList
              onSelect={(selected) => {
                if (selected === 'Custom') {
                  setIsCustom(true);
                  setGender('');
                  dd.close();
                  requestAnimationFrame(() => inputRef.current?.focus?.());
                  return;
                }
                setGender(selected);
                setIsCustom(false);
                dd.close();
              }}
            />
          </div>
        )}
      </div>
      <VisibilityRadioGroup
        value={visibility}
        onChange={setVisibility}
        disabled={!gender}
      />
      <EditorActions onSave={handleSave} />
    </div>
  );
};

/* ================================================================== */
/* Korean Skills (+ TOPIK)                                            */
/* ================================================================== */

export const KoreanSkillsEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [koreanSkills, setKoreanSkills] = useState(user?.koreanSkills || '');
  const [topikInfo, setTopikInfo] = useState(
    user?.topikLevel || user?.topikDate
      ? {
          type: (user?.koreanSkills || '').includes('Speaking')
            ? 'topikSpeaking'
            : 'topik',
          level: user?.topikLevel || '',
          date: user?.topikDate || '',
        }
      : null,
  );
  const [visibility, setVisibility] = useState(
    user?.visibility?.koreanSkills || 'PUBLIC',
  );
  const [topikModalOpen, setTopikModalOpen] = useState(false);

  const skillsDd = useDropdown();

  const handleSave = () =>
    onSave({
      koreanSkills,
      topikInfo: topikInfo
        ? { level: topikInfo.level, date: topikInfo.date || null }
        : null,
      visibility: mergeVisibility(user, 'koreanSkills', visibility),
    });

  return (
    <div className='flex flex-col w-full pt-4 pb-10'>
      <div className='relative w-full' ref={skillsDd.ref}>
        <Input
          id='korean-skills-input'
          label=''
          placeholder='Select your korean level'
          value={koreanSkills}
          state={getInputState(false, skillsDd.open)}
          readOnly
          onClick={skillsDd.toggle}
          hideCount
          showStatusIcon={false}
          rightIcon={<ArrowToggleIcon open={skillsDd.open} />}
          onRightIconClick={skillsDd.toggle}
        />
      </div>

      {(skillsDd.open || topikInfo) && (
        <div className='flex flex-col gap-2 w-full mt-1.5'>
          {skillsDd.open && (
            <div className='z-10 w-full'>
              <KoreanSkillsList
                isMyPagePersonalInfo={true}
                onSelect={(selected) => {
                  let nextTopik = null;
                  if (selected === 'TOPIK certified') {
                    nextTopik = { type: 'topik', level: '', date: '' };
                  } else if (selected === 'TOPIK Speaking certified') {
                    nextTopik = { type: 'topikSpeaking', level: '', date: '' };
                  }
                  setKoreanSkills(selected);
                  setTopikInfo(nextTopik);
                  setTopikModalOpen(false);
                  skillsDd.close();
                }}
              />
            </div>
          )}

          {topikInfo && (
            <div className='flex flex-col'>
              {(topikInfo.level || topikInfo.date) && (
                <div className='flex flex-col gap-2 pt-2 pb-4 w-full mt-1.5'>
                  {topikInfo.level && (
                    <div className='flex items-center gap-2 w-full'>
                      <span className='shrink-0 px-1.5 py-0.5 rounded-[5px] bg-surface-green10 text-green-deep02 border border-surface-green10 typo-b11Strong'>
                        Level
                      </span>
                      <span className='flex-1 min-w-0 typo-b14Strong text-text-placeholder'>
                        {topikInfo.level}
                      </span>
                      <button
                        type='button'
                        onClick={() =>
                          setTopikInfo((prev) => ({ ...prev, level: '' }))
                        }
                        aria-label='Delete level'
                        className='shrink-0 w-6 h-6 flex items-center justify-center'
                      >
                        <TrashIcon className='w-5 h-5 text-icon-placeholder' />
                      </button>
                    </div>
                  )}

                  {topikInfo.date && (
                    <div className='flex items-center gap-2 w-full'>
                      <span className='shrink-0 px-1.5 py-0.5 rounded-[5px] bg-surface-green10 text-green-deep02 border border-surface-green10 typo-b11Strong'>
                        Issue date
                      </span>
                      <span className='flex-1 min-w-0 typo-b14Strong text-text-placeholder'>
                        {topikInfo.date}
                      </span>
                      <button
                        type='button'
                        onClick={() =>
                          setTopikInfo((prev) => ({ ...prev, date: '' }))
                        }
                        aria-label='Delete issue date'
                        className='shrink-0 w-6 h-6 flex items-center justify-center'
                      >
                        <TrashIcon className='w-5 h-5 text-icon-placeholder' />
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className='group flex px-4 py-3 items-center rounded-lg bg-grayscale-gray02/40 border-grayscale-gray02 border hover:bg-grayscale-gray02/70'>
                <button
                  type='button'
                  onClick={() => setTopikModalOpen(true)}
                  className='flex items-center gap-2 typo-b14Strong text-text-placeholder w-full'
                >
                  <span className='flex w-5 h-5 justify-center items-center bg-[hsla(38,9%,82%,0.4)] group-hover:bg-[hsla(38,9%,82%,0.6)] rounded-[10px]'>
                    {topikInfo.date ? (
                      <EditIcon className='w-[14px] h-[14px] text-icon-placeholder' />
                    ) : (
                      <PlusIcon className='w-[14px] h-[14px] text-icon-placeholder' />
                    )}
                  </span>
                  {topikInfo.date ? 'Edit certification' : 'Add certification'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className='mt-[30px]'>
        <VisibilityRadioGroup
          value={visibility}
          onChange={setVisibility}
          disabled={!koreanSkills}
        />
      </div>

      <div className='mt-[30px]'>
        <EditorActions onSave={handleSave} />
      </div>

      {topikModalOpen && (
        <TopikCertModal
          topikInfo={topikInfo}
          setTopikInfo={setTopikInfo}
          onClose={() => setTopikModalOpen(false)}
        />
      )}
    </div>
  );
};

/* ================================================================== */
/* Nationality                                                        */
/* ================================================================== */

export const NationalityEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [nationality, setNationality] = useState(user?.nationality || '');
  const [visibility, setVisibility] = useState(
    user?.visibility?.nationality || 'PUBLIC',
  );
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  const handleSave = () =>
    onSave({
      nationality,
      visibility: mergeVisibility(user, 'nationality', visibility),
    });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <NationalityInputBlock
        id='nationality-input'
        value={nationality}
        inputRef={(el) => (ref.current = el)}
        state={focused ? 'active' : getInputState(focused, false)}
        focused={focused}
        onChange={(e) => setNationality(e.target.value)}
        onSelect={(val) => {
          setNationality(val);
          setFocused(false);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        isMyPage={true}
        visibility={visibility}
        onVisibilityChange={setVisibility}
        disabled={!nationality}
        isMyPagePersonalInfo={true}
      />
      <EditorActions onSave={handleSave} />
    </div>
  );
};

/* ================================================================== */
/* Role                                                               */
/* ================================================================== */

export const RoleEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [careerRole, setCareerRole] = useState(user?.careerRole || '');
  const [customRole, setCustomRole] = useState(user?.customRole || '');
  const [visibility, setVisibility] = useState(
    user?.visibility?.careerRole || 'PUBLIC',
  );
  const dd = useDropdown();

  const handleSave = () =>
    onSave({
      careerRole,
      ...(careerRole === 'Other (Enter manually)' ? { customRole } : {}),
      visibility: mergeVisibility(user, 'careerRole', visibility),
    });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <div className='flex flex-col w-full gap-2' ref={dd.ref}>
        <Input
          label=''
          id='career-role-input'
          placeholder='Select your role'
          value={careerRole}
          showStatusIcon={false}
          readOnly
          rightIcon={<ArrowToggleIcon open={dd.open} />}
          onClick={dd.toggle}
          onRightIconClick={dd.toggle}
        />

        {dd.open && (
          <div className='-mt-1 z-20 w-full'>
            <RoleList
              selectedOption={careerRole}
              onSelect={(opt) => {
                setCareerRole(opt);
                dd.close();
              }}
              isMyPagePersonalInfo={true}
            />
          </div>
        )}

        {careerRole === 'Other (Enter manually)' && (
          <Input
            label=''
            placeholder='Enter role title (e.g. Legal Counsel)'
            value={customRole}
            showStatusIcon={false}
            onChange={(e) => setCustomRole(e.target.value)}
          />
        )}
      </div>

      <VisibilityRadioGroup
        value={visibility}
        onChange={setVisibility}
        disabled={!careerRole}
      />

      <EditorActions onSave={handleSave} />
    </div>
  );
};

/* ================================================================== */
/* Career level                                                       */
/* ================================================================== */

export const CareerLevelEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [careerLevel, setCareerLevel] = useState(user?.careerLevel || '');
  const [visibility, setVisibility] = useState(
    user?.visibility?.careerLevel || 'PUBLIC',
  );
  const dd = useDropdown();

  const handleSave = () =>
    onSave({
      careerLevel,
      visibility: mergeVisibility(user, 'careerLevel', visibility),
    });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <div className='w-full' ref={dd.ref}>
        <Input
          label=''
          id='career-level-input'
          placeholder='Select your career level'
          value={careerLevel}
          state={getInputState(false, dd.open)}
          readOnly
          showStatusIcon={false}
          rightIcon={<ArrowToggleIcon open={dd.open} />}
          onClick={dd.toggle}
          onRightIconClick={dd.toggle}
        />

        {dd.open && (
          <div className='mt-1 z-10 w-full'>
            <CareerList
              selectedOption={careerLevel || 'Select Career level'}
              onSelect={(opt) => {
                setCareerLevel(opt);
                dd.close();
              }}
              isMyPagePersonalInfo={true}
            />
          </div>
        )}
      </div>

      <VisibilityRadioGroup
        value={visibility}
        onChange={setVisibility}
        disabled={!careerLevel}
      />
      <EditorActions onSave={handleSave} />
    </div>
  );
};

/* ================================================================== */
/* Visa info                                                          */
/* ================================================================== */

export const VisaInfoEditor = ({ onSave }) => {
  const { user } = useAuth();
  const [visaInfo, setVisaInfo] = useState(
    user?.visaInfo || { type: '', expiryDate: null },
  );
  const [visibility, setVisibility] = useState(
    user?.visibility?.visaInfo || 'PUBLIC',
  );
  const typeDd = useDropdown();
  const expiryDd = useDropdown();

  const expiryDate =
    visaInfo?.expiryDate && !isNaN(new Date(visaInfo.expiryDate))
      ? new Date(visaInfo.expiryDate)
      : null;
  const expiryIconState = getIconState(
    expiryDate,
    getInputState(false, typeDd.open),
  );

  const handleSave = () =>
    onSave({
      visaInfo: {
        type: visaInfo?.type,
        expiryDate: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : null,
      },
      visibility: mergeVisibility(user, 'visaInfo', visibility),
    });

  return (
    <div className='flex flex-col gap-[30px] w-full pt-4 pb-10 border-b border-grayscale-gray02'>
      <div className='grid grid-cols-1 gap-4 items-end w-full'>
        {/* Visa type */}
        <div className='relative w-full' ref={typeDd.ref}>
          <Input
            label='Visa type'
            id='visa-info-input'
            placeholder='Select your visa type'
            value={visaInfo?.type}
            readOnly
            showStatusIcon={false}
            state={getInputState(false, typeDd.open)}
            rightIcon={<ArrowToggleIcon open={typeDd.open} />}
            onClick={typeDd.toggle}
            onRightIconClick={typeDd.toggle}
          />

          {typeDd.open && (
            <div className='mt-1 z-10 w-full'>
              <VisaTypeList
                selectedOption={visaInfo?.type}
                onSelect={(value) => {
                  setVisaInfo((prev) => ({ ...prev, type: value }));
                  typeDd.close();
                }}
                onClose={typeDd.close}
                isMyPagePersonalInfo={true}
              />
            </div>
          )}
        </div>

        {/* Expiry date */}
        <div className='relative w-full' ref={expiryDd.ref}>
          <Input
            label='expiry date'
            placeholder='Select expiry date'
            value={expiryDate ? format(expiryDate, 'dd MMM yyyy') : ''}
            readOnly
            showStatusIcon={false}
            rightIcon={<StyledCalendarIcon $state={expiryIconState} />}
            onClick={expiryDd.toggle}
            onRightIconClick={expiryDd.toggle}
          />

          {expiryDd.open && (
            <div className='absolute top-8 left-0 z-10 w-full'>
              <CustomDatePicker
                $isMobile
                isMyPagePersonalInfoVisa={true}
                isVisaExpiryDate
                isMyPage
                selectedDate={expiryDate}
                onChange={(date) => {
                  setVisaInfo((prev) => ({ ...prev, expiryDate: date }));
                  expiryDd.close();
                }}
                onClose={expiryDd.close}
                onClickOutside={expiryDd.close}
              />
            </div>
          )}
        </div>
      </div>

      <VisibilityRadioGroup
        isVisaInfo
        value={visibility}
        onChange={setVisibility}
        disabled={!visaInfo?.type}
      />
      <EditorActions onSave={handleSave} />
    </div>
  );
};
